import React, { useMemo, useState, useEffect, useContext } from "react";
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../../../components/common/DataTable";
import { GET_PRODUCTS_LIST, READ_EXPORT_SHIPMENT_COSTING } from "../../../services/apiRoutes";
import { AuthContext } from "../../../context/AuthContext";
import CustomLoader from "../../common/CustomLoader";
import axios from "axios";
import CustomReport from "../../common/CustomReport";
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { Box, Card, CardContent, Grid, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import CustomAutocomplete from "../../common/CustomAutoComplete";
import { genericPost } from "../../../services/authServices";


const title = "Export Shipment Costing Booking Wise";

const ExportShipmentCostingReportView = () => {
    const { commercialInvExpId } = useParams();
    const companyDateFormat = useCompanyDateFormat();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [productList, setProducts] = useState([]);
    const [dataSource, setDataSource] = useState(null);
    const [filterPayload, setFilterPayload] = useState({
        particularName: null,
    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value,
        }));
    };



    const fetchProductsList = async () => {
        const authToken = sessionStorage.getItem('accessToken');
        const payload = {
            clientCompanyId: 0,
        };
        try {
            const response = await axios.post(GET_PRODUCTS_LIST, payload, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            if (response.status === 200 && response.data.success) {
                const productData = response.data.productsList;
                const formattedProducts = productData.map((data) => ({
                    id: data.productsId,
                    title: data.productName,
                }));

                setProducts(formattedProducts);
            } else {
                console.error('Failed to fetch products list:', response.data);
            }
        } catch (error) {
            console.error('Error fetching products list:', error);
        }
    };


    const fetchData = async (commercialInvExpId, particularName = null) => {
        try {
            setLoading(true);
            const payload = {
                commercialInvExpId: commercialInvExpId,
                ...(particularName && { particularName })
            };
            const response = await genericPost(READ_EXPORT_SHIPMENT_COSTING, payload, {});

            if (response.data.success && response.data.commercialInvExps.length > 0) {
                const mappedData = response.data.commercialInvExps.map((invoiceData, index) => {
                    const currencySymbol = invoiceData?.currency?.currency_symbol || '';
                    const baseCurrencySymbol =
                        invoiceData?.exportShipmentList?.company?.baseCurrencyData?.currency_symbol || '';

                    const totalQty = invoiceData?.particulars?.reduce((acc, item) => acc + Number(item.qty || 0), 0) || 0;
                    const proformaInvoiceNo = invoiceData?.ProformaInvExp?.proformaInvoiceNo || "";
                    const proformaAmount = invoiceData?.particulars?.reduce(
                        (sum, item) => sum + (Number(item.amount) || 0), 0
                    );
                    const totalVouchers = invoiceData.receiptVoucher?.length || 0;

                    const totalConversionRate = invoiceData.receiptVoucher?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRatePayment = totalConversionRate > 0 ? (totalConversionRate / totalVouchers).toFixed(2) : '';
                    const totalBooking = invoiceData.shippingBooking?.length || 0;
                    const totalBookingConversionRate = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.conversionRate) || 0), 0) || 0;
                    const halfConversionRateBooking = totalBookingConversionRate > 0 ? (totalBookingConversionRate / totalBooking).toFixed(2) : "1";
                    const halfConversionRate =
                        Number(halfConversionRatePayment) > 0
                            ? Number(halfConversionRatePayment)
                            : Number(halfConversionRateBooking) > 0
                                ? Number(halfConversionRateBooking)
                                : 1;


                    const totalDutyLenght = invoiceData.duty?.length || 0;

                    const boeConversionRate = invoiceData?.duty?.reduce((sum, item) => {
                        return sum + (parseFloat(item.boeConversionRate || 0));
                    }, 0) || 0;

                    const totalBoeConversionRate = boeConversionRate > 0 ? (boeConversionRate / totalDutyLenght).toFixed(2) : 1;

                    const advanceAmount = invoiceData.advanceReceipt?.reduce(
                        (sum, item) => sum + (Number(item.referenceAmount) || 0), 0
                    ) || 0;

                    const advanceAmountBaseCurrency = invoiceData.advanceReceipt?.reduce(
                        (sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0
                    ) || 0;


                    const finalAmount = invoiceData.finalReceipt?.reduce(
                        (sum, item) => sum + (Number(item.referenceAmount) || 0), 0
                    ) || 0;

                    const finalAmountBaseCurrency = invoiceData.finalReceipt?.reduce(
                        (sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0
                    ) || 0;

                    const shippingLineCharges = invoiceData.shippingBooking?.reduce((sum, item) => sum + (Number(item.shippingLineCharges) || 0), 0) || 0;

                    const bankCharges = invoiceData.receiptVoucher?.reduce((sum, item) => sum + (Number(item.bankCharges) || 0), 0) || 0;

                    const totalDutyAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((dutySum, p) => dutySum + parseFloat(p.duty || 0), 0) || 0);
                    }, 0) || 0;

                    const totalTaxAmount = invoiceData?.duty?.reduce((sum, dutyItem) => {
                        return sum + (dutyItem?.particulars?.reduce((taxSum, p) => taxSum + parseFloat(p.tax || 0), 0) || 0);
                    }, 0) || 0;

                    const totalExpenses = invoiceData?.expenseVoucher?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;

                    const totalExpensesInBase = invoiceData?.expenseVoucher?.reduce((sum, item) => sum + (Number(item.amountInBaseCurrency) || 0), 0) || 0;

                    const formatAmount = (amt, symbol) => `${symbol} ${Number(amt || 0).toFixed(2)}`;

                    const seaFreightChargesLedgerStatus = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 5)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const seaFreightChargesLedgerBaseCurrency = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 5)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const TransportCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 4)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const TransportChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 4)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const CFSCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 3)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const CFSChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 3)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const CHACharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 2)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const CHAChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 2)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const ShippingLineandAgencyCharges = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 1)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const ShippingLineandAgencyChargesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 1)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const miscellaneousExpenses = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 0)
                        ?.reduce((sum, item) => sum + Number(item.amount || 0), 0) || 0;

                    const miscellaneousExpensesInBase = invoiceData?.expenseVoucher
                        ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === 0)
                        ?.reduce((sum, item) => sum + Number(item.amountInBaseCurrency || 0), 0) || 0;

                    const unitName = invoiceData?.particulars?.[0]?.productsUnit?.unitName || '';
                    const getCurrencySymbolsByStatus = (expenseVoucherList, status) => {
                        const currencyList = expenseVoucherList
                            ?.filter(item => item?.expenseLedger?.expenseLedgerStatus === status)
                            ?.map(item => item?.currency?.currency_symbol || '') || [];

                        const uniqueSymbols = [...new Set(currencyList)];
                        return uniqueSymbols.length === 1
                            ? uniqueSymbols[0]
                            : uniqueSymbols.join(', ');
                    };

                    const totalAmount = invoiceData?.particulars?.reduce((acc, item) => acc + (Number(item.qty || 0) * parseFloat(item.rate || 0)), 0) || 0;

                    const seaFreightSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 5);
                    const transportChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 4);
                    const cfsChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 3);
                    const chaChargesSymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 2);
                    const shippingLineAgencySymbol = getCurrencySymbolsByStatus(invoiceData?.expenseVoucher, 1);

                    const currencyTaxSymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTaxList?.currency_symbol || '';
                    const currencyDutySymbol = invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDutyList?.currency_symbol || '';
                    let totalTaxAmountInBase = 0;
                    let totalDutyAmountInBase = 0;
                    let taxBaseCurrency = 0;
                    let dutyBaseCurrency = 0;
                    const matchBaseCurrency = parseInt(invoiceData?.exportShipmentList?.company?.baseCurrency || '');
                    const matchTaxCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyTax || '');
                    const matchDutyCurrency = parseInt(invoiceData?.duty?.[0]?.particulars?.[0]?.currencyDuty || '');

                    if (matchBaseCurrency === matchTaxCurrency) {
                        totalTaxAmountInBase = totalTaxAmount;
                        taxBaseCurrency = currencyTaxSymbol;
                    } else {
                        totalTaxAmountInBase = totalTaxAmount * totalBoeConversionRate;
                        taxBaseCurrency = baseCurrencySymbol;
                    }

                    if (matchBaseCurrency === matchDutyCurrency) {
                        totalDutyAmountInBase = totalDutyAmount;
                        dutyBaseCurrency = currencyDutySymbol;
                    } else {
                        totalDutyAmountInBase = totalDutyAmount * totalBoeConversionRate;
                        dutyBaseCurrency = baseCurrencySymbol;
                    }

                    const totalShipmentExpense = (proformaAmount * halfConversionRate) + bankCharges + totalExpensesInBase + totalDutyAmountInBase || "";

                    const primaryQty = Number(invoiceData?.particulars?.[0]?.products?.primaryQty || 0);
                    const costPerUnit = totalQty && totalQty !== 0
                        ? ((totalShipmentExpense) / totalQty).toFixed(2)
                        : '0.00';

                    const costPerUnitPrimary = (totalQty && totalQty !== 0 && primaryQty)
                        ? (parseFloat(costPerUnit) / primaryQty).toFixed(2)
                        : '0.00';

                    const secondaryQty = Number(invoiceData?.particulars?.[0]?.products?.secondaryQty || 0);

                    const secondaryUnit = invoiceData?.particulars?.[0]?.products?.secondaryUnitList?.unitName || "";
                    const primaryUnit = invoiceData?.particulars?.[0]?.products?.primaryUnitList?.unitName || "";
                    const productsUnit = invoiceData?.particulars?.[0]?.products?.productsUnit?.unitName || "";

                    const costPerUnitSecondary = (totalQty && totalQty !== 0 && secondaryQty)
                        ? (parseFloat(costPerUnit) / secondaryQty).toFixed(2)
                        : '0.00';

                    return {
                        index: (index + 1).toString().padStart(2, '0'),
                        etaDate: companyDateFormat(invoiceData?.date) || '',
                        shipmentId: invoiceData?.shipmentId || '',
                        piNumber: proformaInvoiceNo,
                        clientCompanyName: invoiceData?.customer?.clientCompanyName || "",
                        billingCountry: invoiceData?.customer?.billingCountry?.name || "",
                        // numberOfContainers: invoiceData?.shippingBooking?.reduce((sum, item) => sum + Number(item.numberOfContainers || 0), 0) || 0,
                        totalQty: `${totalQty} ${unitName}`,
                        piAmount: formatAmount(proformaAmount, currencySymbol),
                        piAmountBaseCurrency: formatAmount(proformaAmount * halfConversionRate, baseCurrencySymbol),
                        paymentAmount: formatAmount(advanceAmount, currencySymbol),
                        amountInBaseCurrency: formatAmount(advanceAmountBaseCurrency, baseCurrencySymbol),
                        finalAmount: formatAmount(finalAmount, currencySymbol),
                        finalAmountBaseCurrency: formatAmount(finalAmountBaseCurrency, baseCurrencySymbol),
                        bankCharges: formatAmount(bankCharges, baseCurrencySymbol),
                        shippingLineCharges: formatAmount(shippingLineCharges, currencySymbol),
                        agencyName: invoiceData?.shippingBooking?.map(item => item?.freightForwardAgency?.agencyName).join(", ") || "",
                        chaName: invoiceData?.shippingBooking?.map(item => item?.customClearingAgency?.chaName).join(", ") || "",

                        totalExpenses: formatAmount(totalShipmentExpense, currencySymbol),

                        totalExpensesInBase: formatAmount(totalShipmentExpense, baseCurrencySymbol),
                        totalExpensesWithTax: formatAmount((totalShipmentExpense) + totalTaxAmountInBase, baseCurrencySymbol),

                        seaFreight: formatAmount(seaFreightChargesLedgerStatus, seaFreightSymbol),
                        seaFreightBaseCurrency: formatAmount(seaFreightChargesLedgerBaseCurrency, baseCurrencySymbol),
                        transportCharges: formatAmount(TransportCharges, transportChargesSymbol),
                        transportChargesInBase: formatAmount(TransportChargesInBase, baseCurrencySymbol),

                        cFSCharges: formatAmount(CFSCharges, cfsChargesSymbol),
                        cFSChargesInBase: formatAmount(CFSChargesInBase, baseCurrencySymbol),

                        cHACharges: formatAmount(CHACharges, chaChargesSymbol),
                        cHAChargesInBase: formatAmount(CHAChargesInBase, baseCurrencySymbol),

                        shippingLineandAgencyCharges: formatAmount(ShippingLineandAgencyCharges, shippingLineAgencySymbol),
                        shippingLineandAgencyChargesInBase: formatAmount(ShippingLineandAgencyChargesInBase, baseCurrencySymbol),

                        miscellaneousExpenses: formatAmount(miscellaneousExpenses || 0, currencySymbol),
                        miscellaneousExpensesInBase: formatAmount(miscellaneousExpensesInBase || 0, baseCurrencySymbol),

                        costPerUnit: `${formatAmount(costPerUnit, baseCurrencySymbol)}${productsUnit ? ' ' + productsUnit : ''}`,
                        costPerUnitSecondary: `${formatAmount(costPerUnitSecondary, baseCurrencySymbol)}${secondaryUnit ? ' ' + secondaryUnit : ''}`,
                        costPerUnitPrimary: `${formatAmount(costPerUnitPrimary, baseCurrencySymbol)}${primaryUnit ? ' ' + primaryUnit : ''}`,

                        totalShipmentExpense: formatAmount(totalShipmentExpense || 0, currencySymbol),
                        totalTax: formatAmount(totalTaxAmount, currencyTaxSymbol),
                        totalTaxAmountInBase: formatAmount(totalTaxAmountInBase || 0, taxBaseCurrency),

                        totalDuty: formatAmount(totalDutyAmount, currencyDutySymbol),
                        totalDutyAmountInBase: formatAmount(totalDutyAmountInBase || 0, dutyBaseCurrency),
                        commercialInvoiceNo: invoiceData?.commercialInvoiceNo || "",
                        bookingNumber: invoiceData?.booking?.bookingNumber || "",
                        numberOfContainers: invoiceData?.booking?.numberOfContainers || 0,

                    };
                });

                setData(mappedData);
                setDataSource(response.data);

            } else {
                setError("No commercial invoice data found.");
                setData([]);
            }
        } catch (err) {
            setError(err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const productRows = useMemo(() => {
        if (!dataSource?.CommercialInvoiceParticulars) return [];

        return dataSource.CommercialInvoiceParticulars.map((item) => {
            const qty = Number(item?.qty || 0);
            const amount = Number(item?.amount || 0);
            const costPerUnit = qty ? (amount / qty).toFixed(2) : "0.00";
            const primaryQty = Number(item?.products?.primaryQty || 0);
            const secondaryQty = Number(item?.products?.secondaryQty || 0);
            const baseUnit = item?.products?.productsUnit?.unitName || "";
            const primaryUnit = item?.products?.primaryUnitList?.unitName || "";
            const secondaryUnit = item?.products?.secondaryUnitList?.unitName || "";
            return {
                productName: item?.products?.productName,
                costPerUnit: `${costPerUnit} ${baseUnit}`,
                costPerUnitPrimary: primaryQty
                    ? `${(costPerUnit / primaryQty).toFixed(2)} ${primaryUnit}`
                    : `0.00 ${primaryUnit}`,
                costPerUnitSecondary: secondaryQty
                    ? `${(costPerUnit / secondaryQty).toFixed(2)} ${secondaryUnit}`
                    : `0.00 ${secondaryUnit}`,
            };
        });
    }, [dataSource]);

    useEffect(() => {
        if (!commercialInvExpId) return;
        setLoading(true);
        fetchData(commercialInvExpId, filterPayload.particularName);
        fetchProductsList();
    }, [commercialInvExpId, token, filterPayload.particularName]);

    const defaultView = 'exportshipmentcostingreport';
    const selectedRow = data?.[0] || {};


    const mainContent = (
        <>
            <CustomLoader open={loading} />

            <Card variant="outlined"
                sx={{
                    borderRadius: 2,
                    "& .MuiTypography-root": {
                        fontSize: "14px", textTransform: "capitalize",
                    },
                    "& .MuiTableCell-root": {
                        fontSize: "14px", textTransform: "capitalize",
                        padding: "4px 8px"
                    }
                }}
            >
                <CardContent>

                    {/* HEADER */}

                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <Typography><span style={{ color: "#9e9e9e" }}>Shipment ID:</span>{" "}
                                {selectedRow.shipmentId}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography>    <span style={{ color: "#9e9e9e" }}>Customer:</span>{" "}
                                {selectedRow.clientCompanyName}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography><span style={{ color: "#9e9e9e" }}>PI No:</span>{" "}
                                {selectedRow.piNumber}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <CustomAutocomplete
                                options={productList}
                                getOptionLabel={(option) => option.title}
                                placeholder="Select Product Name"
                                value={productList.find((item) => item.id === filterPayload.particularName) || null}
                                onChange={(event, newValue) =>
                                    handleFilterChange("particularName", newValue?.id || null)
                                }
                            />
                        </Grid>

                        <Grid item xs={3}>
                            <Typography>
                                <span style={{ color: "#9e9e9e" }}>
                                    CI No:
                                </span>{" "}
                                {selectedRow.commercialInvoiceNo}
                            </Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography>
                                <span style={{ color: "#9e9e9e" }}>
                                    Booking No:
                                </span>{" "}
                                {selectedRow.bookingNumber}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography><span style={{ color: "#9e9e9e" }}>No. of Container:</span>{" "}
                                {selectedRow.numberOfContainers}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography><span style={{ color: "#9e9e9e" }}>Total Qty:</span>{" "}
                                {selectedRow.totalQty}</Typography>
                        </Grid>

                        <Grid item xs={3}>
                            <Typography><span style={{ color: "#9e9e9e" }}>Country:</span>{" "}
                                {selectedRow.billingCountry}</Typography>
                        </Grid>
                    </Grid>

                    {/* MAIN SECTION */}
                    <Grid container spacing={2} mt={2}>

                        {/* COST TABLE */}
                        <Grid item xs={8}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Particular</b></TableCell>
                                        <TableCell ><b>Amount</b></TableCell>
                                        <TableCell><b>Amount In Base Currency</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>PI Amount</TableCell>
                                        <TableCell align="center">{selectedRow.piAmount}</TableCell>
                                        <TableCell align="center">{selectedRow.piAmountBaseCurrency}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Advance Amount</TableCell>
                                        <TableCell align="center">{selectedRow.paymentAmount}</TableCell>
                                        <TableCell align="center">{selectedRow.amountInBaseCurrency}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Final Amount</TableCell>
                                        <TableCell align="center">{selectedRow.finalAmount}</TableCell>
                                        <TableCell align="center">{selectedRow.finalAmountBaseCurrency}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Sea Freight</TableCell>
                                        <TableCell align="center">{selectedRow.seaFreight}</TableCell>
                                        <TableCell align="center">{selectedRow.seaFreightBaseCurrency}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Transport Charges</TableCell>
                                        <TableCell align="center">{selectedRow.transportCharges}</TableCell>
                                        <TableCell align="center">{selectedRow.transportChargesInBase}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Custom Duty</TableCell>
                                        <TableCell align="center">{selectedRow.totalDuty}</TableCell>
                                        <TableCell align="center">{selectedRow.totalDutyAmountInBase}</TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>Tax</TableCell>
                                        <TableCell align="center">{selectedRow.totalTax}</TableCell>
                                        <TableCell align="center">{selectedRow.totalTaxAmountInBase}</TableCell>
                                    </TableRow>
                                    {/* @@@@@@@@@@@@@@@@ */}

                                    <TableRow>
                                        <TableCell>CFS Charges</TableCell>
                                        <TableCell align="center">{selectedRow.cFSCharges}</TableCell>
                                        <TableCell align="center">{selectedRow.cFSChargesInBase}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>CHA Charges</TableCell>
                                        <TableCell align="center">{selectedRow.cHACharges}</TableCell>
                                        <TableCell align="center">{selectedRow.cHAChargesInBase}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Shipping Line Charges</TableCell>
                                        <TableCell align="center">{selectedRow.shippingLineandAgencyCharges}</TableCell>
                                        <TableCell align="center">{selectedRow.shippingLineandAgencyChargesInBase}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Miscellaneous Expenses</TableCell>
                                        <TableCell align="center">{selectedRow.miscellaneousExpenses}</TableCell>
                                        <TableCell align="center">{selectedRow.miscellaneousExpensesInBase}</TableCell>
                                    </TableRow>


                                    <TableRow>
                                        <TableCell>Total Expenses Without Tax</TableCell>
                                        <TableCell align="center">{selectedRow.totalExpensesInBase}</TableCell>
                                        <TableCell align="center"> </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Total Expenses With Tax</TableCell>
                                        <TableCell align="center">{selectedRow.totalExpensesWithTax}</TableCell>
                                        <TableCell align="center"> </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Bank Charges</TableCell>
                                        <TableCell align="center">{selectedRow.bankCharges}</TableCell>
                                        <TableCell align="center">  </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Grid>

                        {/* RIGHT SIDE BOX */}
                        <Grid item xs={4}>
                            <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mb: 2 }}>
                                <Typography fontWeight="bold">Cost Per Unit</Typography>
                                <Typography>{selectedRow.costPerUnit}</Typography>
                            </Box>

                            <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, mb: 2 }}>
                                <Typography fontWeight="bold">Primary Cost Per Unit</Typography>
                                <Typography>{selectedRow.costPerUnitPrimary}</Typography>
                            </Box>

                            <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
                                <Typography fontWeight="bold">Secondary Cost Per Unit</Typography>
                                <Typography>{selectedRow.costPerUnitSecondary}</Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* PRODUCT TABLE */}
                    <Box mt={4}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Product Name</b></TableCell>
                                    <TableCell><b>Cost Per Unit</b></TableCell>
                                    <TableCell><b>primary Cost Per Unit</b></TableCell>
                                    <TableCell><b>secondary Cost Per Unit</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {productRows.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{row.productName}</TableCell>
                                        <TableCell>{row.costPerUnit}</TableCell>
                                        <TableCell>{row.costPerUnitPrimary}</TableCell>
                                        <TableCell>{row.costPerUnitSecondary}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                </CardContent>
            </Card>
            {/* @@@@@@@@@@@@@@@@@@@@@@@@@@@ card end */}
        </>
    );

    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={mainContent} />
        </>
    );
};

export default ExportShipmentCostingReportView;
